<template>
    <Modal ref="Modal" :title="$t('buttons.connect_from_file')" size="full" @onHide="hide">
        <slot v-if="progressBar.showed">
            <div class="modal-body">
                <TaskProgressBar :done="progressBar.completedCount" :total="progressBar.totalCount"/>
            </div>
        </slot>
        <slot v-else>
            <div class="modal-body">
                <slot v-if="!parsedData.length">
                    <div class="file-zone" @click="$refs.File.showFile()">
                        <div class="message">
                            <div class="theme_tail theme_tail_circle" v-if="fileName">
                                <div class="pace_progress" data-progress-text="60%" data-progress="60"></div>
                                <div class="pace_activity"></div>
                            </div>
                            <span v-else>{{ $t('labels.drag_and_drop') }}<span class="text-primary">{{ $t('labels.browse') }}</span></span>
                        </div>
                    </div>
                </slot>
                <Button size="sm" color="light" @click="removeRows" v-if="parsedData.length" :disabled="!selectedRowsCount">
                    <i class="icon-trash mr-2"></i>
                    {{ $t('buttons.delete') }}
                </Button>
            </div>
                <table class="table table-striped table-bordered" v-if="parsedData.length">
                    <thead>
                        <tr>
                            <th><Checkbox :model-value="selectedRowsCount === parsedData.length" @update:model-value="selectAllRows"/></th>
                            <th>{{ $t('labels.id') }}</th>
                            <th>{{ $t('labels.sku') }}</th>
                            <th>{{ $t('labels.name') }}</th>
                            <th>{{ $t('labels.factual_amount') }}</th>
                            <th>{{ $t('labels.validation') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                <tr v-for="(row, index) in parsedData" :key="index">
                    <td>
                        <Checkbox :model-value="selectedRows[index]" @update:model-value="selectRow(index)"/>
                    </td>
                    <td>{{ row.document_index }}</td>
                    <td>{{ row.sku }}</td>
                    <td>
                        <Autocomplete
                            :model-value="row.id"
                            :selected-value="row.name"
                            :selected-values="ignorProducts"
                            option-key="id"
                            url="/catalog/product-items"
                            @update:model-value="(product) => selectProduct(product, index)"
                        />
                    </td>
                    <td>
                        <Input v-model="row.factual_amount"/>
                    </td>
                    <td>
                        <span :class="['badge', row.id && row.factual_amount ? 'bg-success' : 'bg-danger']">
                            {{ row.id && row.factual_amount ? $t('labels.filled') : $t('labels.not_filled') }}
                        </span>
                    </td>
                </tr>
                    </tbody>
                </table>
            <div class="modal-footer" v-if="parsedData.length">
                <Button size="sm" color="teal" @click="upload">
                    {{ $t('buttons.upload') }}
                </Button>
            </div>
        </slot>
    </Modal>
    <File ref="File" @fileIsSelected="selectFile"/>
</template>
<script>
import Modal from '../../../../../components/new/modal/AppModal.vue';
import File from '../../../../../components/new/file/AppFile.vue';
import Checkbox from '../../../../../components/new/form/checkbox/AppCheckbox.vue';
import Input from '../../../../../components/new/form/Input.vue';
import Autocomplete from '../../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import Button from '../../../../../components/new/button/AppButton.vue';
import TaskProgressBar from '../../../../../components/new/progress/TaskProgressBar.vue';
import MessageNotificator from '../../../../../services/MessageNotificator';
import { httpClient } from '../../../../../services/HttpClient.ts';
import { chunk } from '../../../../../utils';
import * as XLSX from 'xlsx';

export default {
    name: 'ImportFromFileModal',
    components: {
        Modal, File, Checkbox, Input, Autocomplete, Button, TaskProgressBar
    },
    props: {
        inventory: {
            type: Object,
            required: true
        }
    },
    emits: ['uploaded'],
    data() {
        return {
            fileName: null,
            parsedData: [],
            selectedRows: {},
            progressBar: {
                showed: false,
                totalCount: 0,
                completedCount: 0
            },
        }
    },
    methods: {
        show(){
            this.$refs.Modal.show();
        },
        hide(){
            this.$refs.Modal.hide();
            this.fileName = null;
            this.parsedData = [];
            this.selectedRows = {};
        },
        selectFile(files) {
            if (files[0].type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
                new MessageNotificator().error(this.$t('alerts.xlsx_file_type_error'));
                return;
            }

            this.fileName = files[0].name;

            this.handleFileUpload(files[0]);
        },
        async handleFileUpload(file) {
            if(!file) return;

            const reader = new FileReader();

            reader.onload = async (element) => {
                const binaryStr = element.target.result;
                const workbook = XLSX.read(binaryStr, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const rawData = XLSX.utils.sheet_to_json(sheet, {
                    header: 1,
                    raw: false,
                    defval: ""
                });

                const productSkus = this.extractSkus(rawData);

                const productsData = await this.fetchProductData(productSkus);

                const productsMap = new Map(productsData.map(product => [product.sku, product]));

                this.parsedData = rawData.map((row, index) => {
                    const product = productsMap.get(row[0]);

                    return {
                        document_index: index + 1,
                        sku: row[0],
                        name: product ? product.name : '',
                        factual_amount: this.formatFactualAmount(row[1]),
                        id: product ? product.id : null,
                    }
                });

                this.hideProgressBar();
            }

            reader.readAsBinaryString(file);
        },
        formatFactualAmount(factualAmount) {
            let result = factualAmount.trim();

            result = result.replace(/\s/g, "");

            result = result.replace(",", ".");

            result = result.replace(/\.(?=.*\.)/g, "");

            let number = parseFloat(result);

            return isNaN(number) ? null : number;
        },
        extractSkus(rawData){
            const skus = [];

            rawData.forEach((row) => {
                skus.push(row[0]);
            });

            return skus;
        },
        async fetchProductData(productSkus) {
            if (!productSkus.length) {
                return [];
            };

            this.showProgressBar(chunk(productSkus, 10).length)

            const results = await Promise.all(chunk(productSkus, 10).map((productSkus) => {
                return httpClient.get('/catalog/product-items', {
                    skus: productSkus,
                    limit: 0
                }).then((response) => {
                    this.continueProgressBar()

                    return response.data.data;
                });
            }, this), this)

            return results.flat();
        },
        selectProduct(product, index) {
            this.parsedData[index].id = product.id;
            this.parsedData[index].name = product.name;
            this.parsedData[index].sku = product.sku;
        },
        async upload() {
            let productItems = this.parsedData.filter((productItemInfo) => productItemInfo.id && productItemInfo.factual_amount),
                existingProducts = {};
            this.showProgressBar(chunk(productItems, 10).length + 1);

            await httpClient.get('/management/inventories/products', {
                inventory_uuids: [this.inventory.uuid],
                limit: 0
            }).then((response) => {
                this.continueProgressBar();
                let products = response.data.data.reduce((acc, productItem) => {
                    acc[productItem.product.id] = productItem;
                    return acc;
                }, {});

                existingProducts = products;
            });

            await Promise.all(chunk(productItems, 10).map((productItems) => {
                let requestBodies = [];

                productItems.forEach((productItem) => {
                    if (existingProducts[productItem.id] !== undefined && existingProducts[productItem.id].factual_amount !== productItem.factual_amount) {
                        requestBodies.push({
                            method: 'PUT',
                            path: `/management/inventories/${this.inventory.uuid}/product-items/${existingProducts[productItem.id].uuid}`,
                            body: {
                                fields: [
                                    {
                                        name: 'factual_amount',
                                        value: productItem.factual_amount
                                    }
                                ]
                            }
                        });
                    } else if (existingProducts[productItem.id] === undefined) {
                        requestBodies.push({
                            method: 'POST',
                            path: `management/inventories/${this.inventory.uuid}/product-items`,
                            body: {
                                product_item_id: productItem.id,
                                factual_amount: productItem.factual_amount
                            }
                        })
                    }
                });

                if (requestBodies.length === 0) {
                    this.continueProgressBar();
                    return;
                }

                return httpClient.post('/batch', { data: requestBodies }).then((response) => {
                    this.continueProgressBar();
                });
            }, this), this)

            this.hideProgressBar();

            this.$emit('uploaded');

            new MessageNotificator().success(this.$t('alerts.success'));

            this.hide();
        },
        removeRows() {
            this.parsedData = this.parsedData.filter((row, index) => !this.selectedRows[index]);
            this.selectedRows = {};
        },
        selectRow(index) {
            if (this.selectedRows[index]) {
                delete this.selectedRows[index];
            } else {
                this.selectedRows[index] = true;
            }
        },
        selectAllRows(checked) {
            if (!checked) {
                this.selectedRows = {};
            } else {
                this.parsedData.forEach((row, index) => {
                    this.selectedRows[index] = true;
                });
            }
        },
        showProgressBar(totalCount)
        {
            this.progressBar.totalCount = totalCount;
            this.progressBar.completedCount = 0;
            this.progressBar.showed = true;
        },
        continueProgressBar()
        {
            this.progressBar.completedCount++;
        },
        hideProgressBar()
        {
            this.progressBar.showed = false;
            this.progressBar.totalCount = 0;
            this.progressBar.completedCount = 0;
        }
    },
    computed: {
        ignorProducts() {
            return this.parsedData.map(row => row.id);
        },
        selectedRowsCount() {
            return Object.keys(this.selectedRows).length;
        },
    }
}
</script>

<style scoped>
    .file-zone{
        border: 2px dashed #009688;
        height: 100px;
        background-color: #e0f2f1;
        text-align: center;
        border-radius: 5px;
        cursor: pointer;
    }
    .message{
        margin-top: 38px;
        color: #777777;
    }
    .message > span > i{
        font-size: 24px;
    }
    .modal-footer:not([class*=bg-]) {
        padding-top: 20px;
    }
</style>
